import { LogOut, ShieldOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppConfig } from '@shared/app/app-config';
import { useAuth } from '@shared/hooks/useAuth';
import { useAuthStore } from '@shared/store/auth.store';
import {
  APPLICATION_DEFINITIONS,
  SWITCHABLE_APPLICATION_CODES,
  switchableApplications,
} from '@shared/config/applications';
import { buildHandoffUrl } from '@shared/auth/session-coordination';
import { openApplication } from '@shared/auth/open-application';
import { PATHS } from '@shared/router/paths';

/** "HRMS or Finance" — the applications the other applications offer. */
const OFFERED_NAMES = SWITCHABLE_APPLICATION_CODES.map(
  (code) => APPLICATION_DEFINITIONS[code].name,
).join(' or ');

/**
 * Shown to a signed-in account that does not hold this application — also
 * when its address is typed or bookmarked. Offers the applications it DOES
 * hold among HRMS and Finance (from `/auth/me`; the Employee Portal is not
 * offered for now), continuing the same Identity session, and a sign-out. No
 * role or permission is consulted. Nothing of this application was requested:
 * the backend would refuse it anyway (ApplicationAccessGuard).
 */
export function NoApplicationAccessPage() {
  const navigate = useNavigate();
  const { name, application } = useAppConfig();
  const { user, logout } = useAuth();
  const applications = useAuthStore((state) => state.authorization.applications);
  const sessionId = useAuthStore((state) => state.activeSessionId);
  const held = switchableApplications(applications);
  const others = held.filter(
    (definition) => definition.code !== application && Boolean(definition.url),
  );

  const signOut = async () => {
    try {
      await logout();
    } finally {
      navigate(PATHS.auth.login, { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-warning/15 text-warning">
          <ShieldOff className="size-6" aria-hidden="true" />
        </div>
        <h1 className="mt-4 text-xl font-semibold">No access to {name}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {user?.email ? <span className="font-medium text-foreground">{user.email}</span> : 'This account'}{' '}
          has not been given access to {name}. Ask an administrator if you need it.
        </p>

        {others.length > 0 ? (
          <div className="mt-6 space-y-2 text-left">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Applications you can open
            </p>
            {others.map((definition) => (
              <a
                key={definition.code}
                href={buildHandoffUrl(definition.url as string, sessionId)}
                onClick={(event) => {
                  event.preventDefault();
                  void openApplication(definition.url as string);
                }}
                className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-muted"
              >
                <definition.icon className="size-5 text-primary" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{definition.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {definition.description}
                  </span>
                </span>
              </a>
            ))}
          </div>
        ) : held.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Your account has no access to {OFFERED_NAMES} yet.
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          <LogOut className="size-4" aria-hidden="true" />
          Sign out
        </button>
      </div>
    </div>
  );
}
